import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  TrendingUp,
  Calculator,
  RefreshCw,
  DollarSign,
  BarChart3
} from 'lucide-react';

const WIP_METHODS = [
  { value: 'POC', label: 'Percentage of Completion (POC)' },
  { value: 'COST_OF_SALES', label: 'Cost of Sales' },
  { value: 'COMPLETED_CONTRACT', label: 'Completed Contract' },
  { value: 'REVENUE_BASED', label: 'Revenue Based' }
];

export default function WIPCalculation() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [wipResult, setWIPResult] = useState(null);

  const [formData, setFormData] = useState({
    project_id: '',
    fiscal_year: new Date().getFullYear(),
    period: new Date().getMonth() + 1,
    wip_method: 'POC',
    include_wbs_details: true
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    try {
      setCalculating(true);
      const response = await psAPI.calculateWIP(formData);
      setWIPResult(response.data);
      toast.success('WIP calculation completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate WIP');
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    return `$${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const periods = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WIP Calculation</h1>
            <p className="text-gray-500">Calculate Work-in-Progress for projects</p>
          </div>
        </div>

        {/* Parameters Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-orange-600" />
              Calculation Parameters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>Project *</Label>
                <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                  <SelectTrigger data-testid="select-project">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Fiscal Year</Label>
                <Select value={formData.fiscal_year.toString()} onValueChange={(v) => setFormData({ ...formData, fiscal_year: parseInt(v) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Period</Label>
                <Select value={formData.period.toString()} onValueChange={(v) => setFormData({ ...formData, period: parseInt(v) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map(p => (
                      <SelectItem key={p} value={p.toString()}>Period {p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>WIP Method</Label>
                <Select value={formData.wip_method} onValueChange={(v) => setFormData({ ...formData, wip_method: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIP_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button onClick={handleCalculate} disabled={!formData.project_id || calculating} className="w-full" data-testid="calculate-wip">
                  {calculating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                  Calculate
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {wipResult && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-5 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Total Costs</p>
                    <p className="text-xl font-bold text-orange-600">{formatCurrency(wipResult.total_costs)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(wipResult.total_revenue)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">WIP Costs</p>
                    <p className="text-xl font-bold text-orange-600">{formatCurrency(wipResult.wip_costs)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">WIP Revenue</p>
                    <p className="text-xl font-bold text-amber-600">{formatCurrency(wipResult.wip_revenue)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">WIP Result</p>
                    <p className={`text-xl font-bold ${wipResult.wip_result >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(wipResult.wip_result)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* WBS Details */}
            {wipResult.wbs_details?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-orange-600" />
                    WBS Element Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3">WBS Element</th>
                          <th className="text-right p-3">Total Costs</th>
                          <th className="text-right p-3">Total Revenue</th>
                          <th className="text-right p-3">Progress</th>
                          <th className="text-right p-3">Earned Value</th>
                          <th className="text-right p-3">WIP Costs</th>
                          <th className="text-right p-3">WIP Revenue</th>
                          <th className="text-right p-3">WIP Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wipResult.wbs_details.map((wbs) => (
                          <tr key={wbs.wbs_element_id} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{wbs.wbs_element_name}</td>
                            <td className="p-3 text-right">{formatCurrency(wbs.total_costs)}</td>
                            <td className="p-3 text-right">{formatCurrency(wbs.total_revenue)}</td>
                            <td className="p-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-orange-600 h-1.5 rounded-full" 
                                    style={{ width: `${wbs.progress_percent || 0}%` }}
                                  />
                                </div>
                                <span>{formatPercent(wbs.progress_percent)}</span>
                              </div>
                            </td>
                            <td className="p-3 text-right">{formatCurrency(wbs.earned_value)}</td>
                            <td className="p-3 text-right text-orange-600">{formatCurrency(wbs.wip_costs)}</td>
                            <td className="p-3 text-right text-amber-600">{formatCurrency(wbs.wip_revenue)}</td>
                            <td className={`p-3 text-right font-medium ${wbs.wip_result >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(wbs.wip_result)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 font-medium">
                        <tr>
                          <td className="p-3">Total</td>
                          <td className="p-3 text-right">{formatCurrency(wipResult.total_costs)}</td>
                          <td className="p-3 text-right">{formatCurrency(wipResult.total_revenue)}</td>
                          <td className="p-3 text-right">-</td>
                          <td className="p-3 text-right">-</td>
                          <td className="p-3 text-right text-orange-600">{formatCurrency(wipResult.wip_costs)}</td>
                          <td className="p-3 text-right text-amber-600">{formatCurrency(wipResult.wip_revenue)}</td>
                          <td className={`p-3 text-right ${wipResult.wip_result >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(wipResult.wip_result)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Metadata */}
            <div className="text-right text-sm text-gray-500">
              Calculated: {wipResult.calculated_at ? new Date(wipResult.calculated_at).toLocaleString() : '-'} | 
              Method: {WIP_METHODS.find(m => m.value === wipResult.wip_method)?.label || wipResult.wip_method}
            </div>
          </>
        )}

        {/* Empty State */}
        {!wipResult && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-gray-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Select a project and run WIP calculation</p>
                <p className="text-sm">WIP calculation determines unrealized costs and revenues</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
